import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';

import {
  Accordion,
  AccordionPanel,
  Input
} from '@salesforce/design-system-react';

import formatTimestamp from '../../../i18n/formatTimestamp';

import './ExperimentPerformanceDetails.scss';

export class ExperimentPerformanceDetails extends Component {
  constructor(props) {
    super(props);

    this.panelSections = [
      'basic-information',
      'test-settings',
      'system-information'
    ];

    this.state = {
      expandedPanels: Object.fromEntries(this.panelSections.map(a => [a, true]))
    };
  }

  getI18nID(suffix) {
    return `components.experiment.performance.details.${suffix}`;
  }

  togglePanel(section) {
    const expandedPanels = { ...this.state.expandedPanels };
    expandedPanels[section] = !this.state.expandedPanels[section];
    this.setState({ expandedPanels });
  }

  formatUserDateTime(user, dt) {
    return user && dt ? `${user}, ${formatTimestamp(this.props.intl)(dt)}` : '';
  }

  render() {
    const { formatMessage, formatNumber } = this.props.intl;
    const { experiment } = this.props;
    const testEngine = experiment.engine_name?.toLowerCase();
    const testTrigger =
      testEngine === 'ecs' ? 'ecs' : experiment.end_trigger?.toLowerCase();
    const testMode = formatMessage({
      id: this.getI18nID(`options.testMode.${testEngine}`)
    });
    const testWinner = formatMessage(
      {
        id: this.getI18nID(
          `options.testMode.endTrigger.${testEngine}.${testTrigger}`
        )
      },
      { endValue: formatNumber(experiment.end_value) }
    );

    return (
      <Accordion id="experiment-details-accordion">
        <AccordionPanel
          expanded={!!this.state.expandedPanels[this.panelSections[0]]}
          id={this.panelSections[0]}
          key={`accordion-${this.panelSections[0]}`}
          onTogglePanel={() => this.togglePanel(this.panelSections[0])}
          summary={formatMessage({
            id: this.getI18nID('basicInformation.title')
          })}
        >
          <section className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
            <div className="slds-col_padded slds-size_1-of-2">
              <Input
                id="test-name-input"
                className="field-input"
                label={formatMessage({
                  id: this.getI18nID('basicInformation.testName')
                })}
                readOnly
                value={experiment.name}
              />
            </div>
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <label className="field-label">
                  {formatMessage({
                    id: this.getI18nID('basicInformation.status')
                  })}
                </label>
                <div className="field-value">
                  {formatMessage({
                    id: this.getI18nID(`testStatus.${experiment.status}`)
                  })}
                </div>
              </div>
            </div>
          </section>
          <div>
            <Input
              id="description-input"
              className="field-input"
              label={formatMessage({
                id: this.getI18nID('basicInformation.description')
              })}
              readOnly
              value={experiment.description}
            />
          </div>
        </AccordionPanel>

        <AccordionPanel
          expanded={!!this.state.expandedPanels[this.panelSections[1]]}
          id={this.panelSections[1]}
          key={`accordion-${this.panelSections[1]}`}
          onTogglePanel={() => this.togglePanel(this.panelSections[1])}
          summary={formatMessage({
            id: this.getI18nID('options.title')
          })}
        >
          <section className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
            <div className="slds-col_padded slds-size_1-of-2">
              <Input
                id="test-mode-input"
                className="field-input"
                label={formatMessage({
                  id: this.getI18nID('options.mode')
                })}
                readOnly
                value={testMode}
              />
            </div>
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <Input
                  id="test-mode-input"
                  className="field-input"
                  label={formatMessage({
                    id: this.getI18nID('options.testMode.endTrigger.title')
                  })}
                  readOnly
                  value={testWinner}
                />
              </div>
            </div>
          </section>
        </AccordionPanel>

        <AccordionPanel
          expanded={!!this.state.expandedPanels[this.panelSections[2]]}
          id={this.panelSections[2]}
          key={`accordion-${this.panelSections[2]}`}
          onTogglePanel={() => this.togglePanel(this.panelSections[2])}
          summary={formatMessage({
            id:
              'components.experiment.performance.details.systemInformation.title'
          })}
        >
          <section className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <label className="field-label">
                  {formatMessage({
                    id: this.getI18nID('systemInformation.createdBy')
                  })}
                </label>
                <div className="field-value">
                  {this.formatUserDateTime(
                    experiment.created_by,
                    experiment.created_at
                  )}
                </div>
              </div>
            </div>
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <label className="field-label">
                  {formatMessage({
                    id: this.getI18nID('systemInformation.lastModifiedBy')
                  })}
                </label>
                <div className="field-value">
                  {this.formatUserDateTime(
                    experiment.created_by,
                    experiment.updated_at
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <label className="field-label">
                  {formatMessage({
                    id: this.getI18nID('systemInformation.publishedBy')
                  })}
                </label>
                <div className="field-value">
                  {this.formatUserDateTime(
                    experiment.published_by,
                    experiment.published_at
                  )}
                  &nbsp;
                </div>
              </div>
            </div>
            <div className="slds-col_padded slds-size_1-of-2">
              <div className="field-static">
                <label className="field-label">
                  {formatMessage({
                    id: this.getI18nID('systemInformation.finishedBy')
                  })}
                </label>
                <div className="field-value">
                  {this.formatUserDateTime(
                    experiment.finished_by,
                    experiment.finished_at
                  )}
                  &nbsp;
                </div>
              </div>
            </div>
          </section>
        </AccordionPanel>
      </Accordion>
    );
  }
}

ExperimentPerformanceDetails.propTypes = {
  actions: PropTypes.object,
  experiment: PropTypes.object.isRequired,
  flags: PropTypes.object,
  modals: PropTypes.object,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ExperimentPerformanceDetails);
